import { Link } from "react-router-dom";
import Room from "@/components/Room";

const CLASSES = [
  ["I", "Extended Viability", "The substrate outlasts the capital. No biological constraint.", false],
  ["II", "Productive Transition", "Decline is under way. The margin between biology and capital narrows.", false],
  ["III", "Attenuated", "Productive capacity is thinning against the capital horizon. Under surveillance, and the last class from which recovery is possible.", true],
  ["IV", "Biosenescent", "The Hard Line is crossed. Decline is irreversible without outside intervention.", false],
  ["V", "Irreversible Decline", "The system is exhausted. No resilience remains. Recovery means rebuilding it.", false],
];

function HardLineChart() {
  return (
    <figure>
      <svg viewBox="0 0 640 300" role="img" aria-label="Biological output rises, peaks and falls past the Hard Line, while the capital assumption keeps rising.">
        <rect x="400" y="20" width="220" height="240" fill="#E9E1D3" />
        <line x1="400" y1="20" x2="400" y2="260" stroke="#A48A58" strokeWidth="2" />
        <text x="410" y="40" fontFamily="Inter,Arial" fontSize="11" letterSpacing="2" fill="#A48A58">THE HARD LINE</text>
        <text x="410" y="56" fontFamily="Inter,Arial" fontSize="11" fill="#4A4744">No recovery beyond this point</text>
        <line x1="40" y1="260" x2="620" y2="260" stroke="#1E1E1E" />
        <line x1="40" y1="20" x2="40" y2="260" stroke="#1E1E1E" />
        <path d="M40 255 C 150 250, 190 70, 290 68 S 380 110, 420 170 S 540 250, 620 252" fill="none" stroke="#1E1E1E" strokeWidth="2.5" />
        <path d="M40 235 C 200 190, 360 130, 460 125 S 580 130, 620 140" fill="none" stroke="#4A4744" strokeWidth="1.5" strokeDasharray="6 5" />
        <text x="175" y="60" fontFamily="Inter,Arial" fontSize="11" fill="#4A4744">Growth · Maturity</text>
        <text x="500" y="118" fontFamily="Inter,Arial" fontSize="11" fill="#4A4744">What capital assumes</text>
        <text x="615" y="232" textAnchor="end" fontFamily="Inter,Arial" fontSize="11" fill="#1E1E1E">What the biology does</text>
        <text x="40" y="285" fontFamily="Inter,Arial" fontSize="10" letterSpacing="2" fill="#4A4744">BIOLOGICAL TIME →</text>
        <text x="620" y="285" textAnchor="end" fontFamily="Inter,Arial" fontSize="10" letterSpacing="2" fill="#A48A58">THE TREE STILL STANDS</text>
      </svg>
      <figcaption>The gap between the two lines is ghost yield: income the biology no longer supports.</figcaption>
    </figure>
  );
}

export default function OriginRisk() {
  return (
    <Room title="Classification Standard" description="Where does what you hold sit in biological time? The Biological Time Classification Standard.">
      <div className="fade">
        <div className="kicker">Origin Risk · BTCS</div>
        <h1 data-testid="origin-risk-h1">Where does what you hold sit in biological time?</h1>
        <div className="goldrule"></div>
        <p className="lede">Every loan, policy and valuation written against food, timber, fibre or water rests on a living system with a clock of its own. The numbers above it read normal until the clock says otherwise.</p>
      </div>

      <section className="q">
        <div className="n">01</div>
        <h2>When was the ground beneath what you hold last stated on paper?</h2>
        <p>Beneath the plantation, the forest or the farm sits a substrate: the soil, the water and the living stock that keep the asset producing. The substrate carries the value, and it is almost never classified. It is assumed.</p>
        <p>An unknown is not neutral. It is exposure nobody has written down, held at a price that assumes the answer is good.</p>
      </section>

      <section className="q">
        <div className="n">02</div>
        <h2>How far ahead of the evidence is the line?</h2>
        <p>A person, a tree, a soil and a river each grow, mature and decline, and past a point they stop recovering. That point is reached long before anyone sees it. The tree still stands and the field is still green, but the line is already behind it.</p>
        <HardLineChart />
      </section>

      <section className="q">
        <div className="n">03</div>
        <h2>Why does no score catch it?</h2>
        <p>Nature is mapped, measured, certified and scored, and scores describe. They average strengths against weaknesses on a smooth scale. Living systems do not average: they fail at their weakest point and cross a line, and a smooth scale cannot show a crossing.</p>
        <p>Debt became a global market once it carried a declared class that any institution could price, hold, insure and trade. Biology has never had one. What is not classified is not priced, and what is not priced is not insured. Most of the living world sits there now.</p>
      </section>

      <section className="q">
        <div className="n">04</div>
        <h2>What would a declared class say?</h2>
        <p>A credit rating states whether a borrower outlasts its debt. The Biological Time Classification Standard states whether a living system outlasts the capital that depends on it.</p>
        <table data-testid="btcs-table">
          <tbody>
            <tr><th></th><th>Class</th><th>Condition</th></tr>
            {CLASSES.map(([n, name, cond, line]) => (
              <tr key={n} className={line ? "line" : undefined}>
                <td className="num">{n}</td>
                <td><strong>{name}</strong></td>
                <td>{cond}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hardline">The Hard Line sits between III and IV</p>
        <ul className="plain">
          <li><strong>The weakest point decides.</strong> A strong reading in one area never offsets a confirmed failure in another.</li>
          <li><strong>The Hard Line is tested, not assumed.</strong> Class IV requires exhaustion, loss of resilience and failure to recover, confirmed at the asset itself.</li>
          <li><strong>Every claim carries its status.</strong> Confirmed, directional or unquantified. Gaps are named, never filled.</li>
          <li><strong>Declared and sealed.</strong> A classification states position and is not revised once outcomes are known. The decision belongs to the holder.</li>
        </ul>
      </section>

      <section className="q">
        <div className="n">05</div>
        <h2>Who is asking, without yet the words for it?</h2>
        <ul className="plain">
          <li>An underwriter asks what the book covers across land no one has classified.</li>
          <li>A grower asks why the orchard returns less on the same care.</li>
          <li>A pension fund asks why the forests it holds are burning.</li>
          <li>A government asks why national yields fall while effort holds.</li>
          <li>A parametric designer asks why payouts and losses drift apart.</li>
        </ul>
        <p>One answer sits beneath all of them: where the living system stands in its time.</p>
      </section>
      <p className="src" style={{ marginTop: 40 }}>Classification only. Never investment, legal or insurance advice.</p>

      <section className="close">
        <p className="ask">A thirty-year commitment against a system with ten productive years left is not a thirty-year asset. If you knew the years left in each holding, which would you hold to term, and which would you sell while the price still assumes thirty?</p>
        <Link className="write" to="/enter/classification-standard" data-testid="origin-risk-write">Request a classification</Link>
      </section>
    </Room>
  );
}
